if (!customElements.get('reviews-image-split-carousel')) {
  class ReviewsImageSplitCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.reviews-carousel__slide'));
      this.index = 0;

      const prev = this.querySelector('.reviews-carousel__arrow--prev');
      const next = this.querySelector('.reviews-carousel__arrow--next');

      if (prev) prev.addEventListener('click', () => this.show(this.index - 1));
      if (next) next.addEventListener('click', () => this.show(this.index + 1));

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.reviews-carousel__slide');
        if (!slide) return;
        this.show(this.slides.indexOf(slide));
      });

      this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

      this.lastWidth = window.innerWidth;
      this.stabilizeHeight();
      window.addEventListener('resize', this.debouncedStabilizeHeight.bind(this));
    }

    // iOS Safari fires a resize event when its toolbar collapses/expands
    // during scroll or touch interaction — this happens on ANY page scroll,
    // not just swiping within the carousel. That only ever changes viewport
    // HEIGHT, never width, and text-wrapping (the only thing that actually
    // changes a slide's needed height) only depends on width — so ignore
    // resize events that didn't change the width at all, rather than just
    // debouncing, which still let plain scrolling re-trigger a remeasurement
    // and visibly resize the panel/background-image.
    debouncedStabilizeHeight() {
      if (window.innerWidth === this.lastWidth) return;
      clearTimeout(this.stabilizeTimeout);
      this.stabilizeTimeout = setTimeout(() => {
        this.lastWidth = window.innerWidth;
        this.stabilizeHeight();
      }, 200);
    }

    // Only one slide is ever in the layout at a time (the rest are
    // display:none), so the panel's height naturally follows whichever
    // review is currently showing — and since the panel can carry an
    // optional background-image with background-size:cover, that image
    // visibly resizes/stutters every time a shorter or taller review swaps
    // in. Measure every slide's real height up front and lock the wrapper
    // to the tallest one so the panel height never changes after that.
    stabilizeHeight() {
      const wrapper = this.querySelector('.reviews-carousel__slides');
      if (!wrapper || !this.slides.length) return;

      const wrapperWidth = wrapper.clientWidth;
      let maxHeight = 0;

      this.slides.forEach((slide) => {
        const original = {
          display: slide.style.display,
          visibility: slide.style.visibility,
          position: slide.style.position,
          width: slide.style.width,
        };
        slide.style.display = 'block';
        slide.style.visibility = 'hidden';
        slide.style.position = 'absolute';
        slide.style.width = `${wrapperWidth}px`;

        maxHeight = Math.max(maxHeight, slide.offsetHeight);

        slide.style.display = original.display;
        slide.style.visibility = original.visibility;
        slide.style.position = original.position;
        slide.style.width = original.width;
      });

      wrapper.style.minHeight = `${maxHeight}px`;
    }

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = false;
    }

    handleTouchMove(event) {
      if (this.touchStartX === undefined) return;
      const deltaX = event.touches[0].clientX - this.touchStartX;
      const deltaY = event.touches[0].clientY - this.touchStartY;

      if (!this.isSwiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        event.preventDefault();
      }
    }

    handleTouchEnd(event) {
      const SWIPE_THRESHOLD = 40;
      if (this.isSwiping && this.touchStartX !== undefined) {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        if (deltaX > SWIPE_THRESHOLD) {
          this.show(this.index - 1);
        } else if (deltaX < -SWIPE_THRESHOLD) {
          this.show(this.index + 1);
        }
      }
      this.touchStartX = undefined;
      this.touchStartY = undefined;
      this.isSwiping = false;
    }

    show(newIndex) {
      if (!this.slides.length) return;
      const previousIndex = this.index;
      this.index = (newIndex + this.slides.length) % this.slides.length;

      if (this.index === previousIndex) return;

      const forwardDistance = (this.index - previousIndex + this.slides.length) % this.slides.length;
      const backwardDistance = (previousIndex - this.index + this.slides.length) % this.slides.length;
      const direction = forwardDistance <= backwardDistance ? 'next' : 'prev';

      this.slides.forEach((slide, i) => {
        const isActive = i === this.index;
        slide.classList.toggle('is-active', isActive);
        slide.classList.remove('slide-dir-next', 'slide-dir-prev');
        if (isActive) {
          slide.classList.add(direction === 'next' ? 'slide-dir-next' : 'slide-dir-prev');
        }
      });
    }
  }

  customElements.define('reviews-image-split-carousel', ReviewsImageSplitCarousel);
}
